<template>
    <div class="profileUser">
        <Box
            title="Configurações da Conta"
            sub-title="Gerencie suas informações pessoais, preferências e configurações de segurança"
            icon="user"
            :variant="BOX_VARIANTS.DEFAULT"
        >
            <TabsComponent
                :tabs="tabsConfig"
                default-tab="general"
                @tab-change="handleTabChange"
            >
                <!-- Tab: Informações Gerais -->
                <template #general>
                    <form @submit.prevent="handleSaveGeneral" class="profileUser_form">
                        <div class="modal-section">
                            <h4>Dados Pessoais</h4>

                            <div class="row">
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="generalData.firstName"
                                            label="Nome *"
                                            placeholder="Seu primeiro nome"
                                            :validation="firstNameValidation"
                                        />
                                    </div>
                                </div>
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="generalData.lastName"
                                            label="Sobrenome *"
                                            placeholder="Seu sobrenome"
                                            :validation="lastNameValidation"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="generalData.email"
                                            label="E-mail *"
                                            type="email"
                                            placeholder="seu@email.com"
                                            :validation="emailValidation"
                                        />
                                    </div>
                                </div>
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="generalData.phone"
                                            label="Telefone"
                                            placeholder="(11) 99999-9999"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="generalData.cpfCnpj"
                                            label="CPF/CNPJ *"
                                            placeholder="000.000.000-00"
                                            icon="user"
                                            :mask="cpfCnpjMask"
                                            :validation="cpfCnpjValidation"
                                        />
                                    </div>
                                </div>
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="generalData.company"
                                            label="Empresa/Organização"
                                            placeholder="Nome da sua empresa ou organização"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="profileUser_actions">
                            <sw-button
                                name="Salvar Alterações"
                                color="blue"
                                :disabled="!isGeneralFormValid || savingGeneral"
                            >
                                <template v-if="savingGeneral">Salvando...</template>
                                <template v-else>Salvar Alterações</template>
                            </sw-button>
                        </div>
                    </form>
                </template>

                <!-- Tab: Configurações de URL -->
                <template #url-settings>
                    <form @submit.prevent="handleSaveUrlSettings" class="profileUser_form">
                        <div class="modal-section">
                            <h4>URL Padrão para Páginas Web</h4>
                            <p class="modal-section-description">
                                Configure o nome padrão que será usado nas URLs das suas páginas de compartilhamento e upload.
                            </p>

                            <AlertMessage
                                :variant="ALERT_VARIANTS.WARNING"
                                title="⚠️ Atenção: Esta alteração afetará todas as suas páginas"
                                description="Alterar este nome afetará <strong>todas as suas páginas web</strong> existentes. Links compartilhados anteriormente podem parar de funcionar. Recomendamos fazer esta alteração apenas se necessário."
                                icon="notification"
                                class="profileUser_urlAlert"
                            />

                            <div class="row">
                                <div class="grid_col_8">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="urlSettings.defaultSlug"
                                            label="Nome padrão da URL *"
                                            placeholder="minha-empresa"
                                            formatSearch=" "
                                            formatReplace="-"
                                            lower
                                            :validation="slugValidation"
                                        />
                                        <div class="profileUser_urlPreview">
                                            <strong>Exemplo de URL:</strong>
                                            <div class="profileUser_urlExample">
                                                <span class="profileUser_urlBase">https://filesave.com.br/share/</span>
                                                <span class="profileUser_urlSlug">{{ urlSettings.defaultSlug || 'minha-empresa' }}</span>
                                                <span class="profileUser_urlSuffix">/documentos-rh</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="grid_col_12">
                                    <div class="profileUser_urlRules">
                                        <sw-icon name="info" />
                                        <div>
                                            <p><strong>Regras para URL:</strong></p>
                                            <ul>
                                                <li>Apenas letras minúsculas, números e hífens</li>
                                                <li>Deve começar com uma letra</li>
                                                <li>Não pode terminar com hífen</li>
                                                <li>Mínimo 3 caracteres, máximo 30</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="profileUser_actions">
                            <sw-button
                                name="Salvar Configuração"
                                color="blue"
                                :disabled="!isUrlFormValid || savingUrl"
                            >
                                <template v-if="savingUrl">Salvando...</template>
                                <template v-else>Salvar Configuração</template>
                            </sw-button>
                        </div>
                    </form>
                </template>

                <!-- Tab: Plano e Faturamento -->
                <template #plan>
                    <div class="modal-section">
                        <h4>Plano Atual</h4>

                        <div class="row">
                            <div class="grid_col_12">
                                <div class="profileUser_currentPlan">
                                    <div class="profileUser_planInfo">
                                        <div class="profileUser_planIcon">
                                            <sw-icon :name="currentPlan.icon" />
                                        </div>
                                        <div class="profileUser_planDetails">
                                            <h5>{{ currentPlan.name }}</h5>
                                            <p>{{ currentPlan.description }}</p>
                                            <div class="profileUser_planPrice">
                                                <span class="profileUser_price">R$ {{ formatPrice(currentPlan.price.monthly) }}</span>
                                                <span class="profileUser_period">/mês</span>
                                            </div>
                                        </div>
                                        <div class="profileUser_planActions">
                                            <sw-button
                                                name="Alterar Plano"
                                                color="blue"
                                                size="sm"
                                                @click="changePlan"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="grid_col_4">
                                <div class="profileUser_usageStat">
                                    <div class="profileUser_statIcon">
                                        <sw-icon name="cloud" />
                                    </div>
                                    <div class="profileUser_statContent">
                                        <span class="profileUser_statNumber">{{ currentUsage.storage.used }}GB</span>
                                        <span class="profileUser_statLabel">de {{ currentUsage.storage.total }}GB usados</span>
                                        <div class="profileUser_progressBar">
                                            <div
                                                class="profileUser_progress"
                                                :style="{ width: (currentUsage.storage.used / currentUsage.storage.total * 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="grid_col_4">
                                <div class="profileUser_usageStat">
                                    <div class="profileUser_statIcon">
                                        <sw-icon name="mail" />
                                    </div>
                                    <div class="profileUser_statContent">
                                        <span class="profileUser_statNumber">{{ currentUsage.emails.used }}</span>
                                        <span class="profileUser_statLabel">de {{ currentUsage.emails.total }} envios mensais</span>
                                        <div class="profileUser_progressBar">
                                            <div
                                                class="profileUser_progress"
                                                :style="{ width: (currentUsage.emails.used / currentUsage.emails.total * 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="grid_col_4">
                                <div class="profileUser_usageStat">
                                    <div class="profileUser_statIcon">
                                        <sw-icon name="notification" />
                                    </div>
                                    <div class="profileUser_statContent">
                                        <span class="profileUser_statNumber">{{ currentUsage.alerts.used }}</span>
                                        <span class="profileUser_statLabel">de {{ currentUsage.alerts.total }} alertas ativos</span>
                                        <div class="profileUser_progressBar">
                                            <div
                                                class="profileUser_progress"
                                                :style="{ width: (currentUsage.alerts.used / currentUsage.alerts.total * 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="grid_col_6">
                                <div class="profileUser_usageStat">
                                    <div class="profileUser_statIcon">
                                        <sw-icon name="share" />
                                    </div>
                                    <div class="profileUser_statContent">
                                        <span class="profileUser_statNumber">{{ currentUsage.sharingPages.used }}</span>
                                        <span class="profileUser_statLabel">de {{ currentUsage.sharingPages.total }} páginas compartilhamento</span>
                                        <div class="profileUser_progressBar">
                                            <div
                                                class="profileUser_progress"
                                                :style="{ width: (currentUsage.sharingPages.used / currentUsage.sharingPages.total * 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="grid_col_6">
                                <div class="profileUser_usageStat">
                                    <div class="profileUser_statIcon">
                                        <sw-icon name="upload" />
                                    </div>
                                    <div class="profileUser_statContent">
                                        <span class="profileUser_statNumber">{{ currentUsage.uploadPages.used }}</span>
                                        <span class="profileUser_statLabel">de {{ currentUsage.uploadPages.total }} páginas upload</span>
                                        <div class="profileUser_progressBar">
                                            <div
                                                class="profileUser_progress"
                                                :style="{ width: (currentUsage.uploadPages.used / currentUsage.uploadPages.total * 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="modal-section">
                            <h4>Aumentar Recursos</h4>
                            <p class="modal-section-description">
                                Ajuste seus recursos conforme necessário. Pague apenas pelo que usar acima do seu plano base.
                            </p>

                            <div class="row">
                                <div class="grid_col_12">
                                    <PlanResources
                                        :plan-id="currentPlan.type"
                                        :initial-resources="planResourcesConfig"
                                        :base-resources="planResourcesConfig"
                                        @update:resources="handleResourcesUpdate"
                                        @update:extra-cost="handleExtraCostUpdate"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Tab: Segurança -->
                <template #security>
                    <form @submit.prevent="handleSaveSecurity" class="profileUser_form">
                        <div class="modal-section">
                            <h4>Alterar Senha</h4>

                            <div class="row">
                                <div class="grid_col_12">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="securityData.currentPassword"
                                            label="Senha atual *"
                                            type="password"
                                            placeholder="Digite sua senha atual"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="securityData.newPassword"
                                            label="Nova senha *"
                                            type="password"
                                            placeholder="Digite a nova senha"
                                            :validation="newPasswordValidation"
                                        />
                                    </div>
                                </div>
                                <div class="grid_col_6">
                                    <div class="modal-field">
                                        <sw-input
                                            v-model="securityData.confirmPassword"
                                            label="Confirmar nova senha *"
                                            type="password"
                                            placeholder="Confirme a nova senha"
                                            :validation="confirmPasswordValidation"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>


                        <div class="profileUser_actions">
                            <sw-button
                                name="Salvar Alterações"
                                color="blue"
                                :disabled="!isSecurityFormValid || savingSecurity"
                            >
                                <template v-if="savingSecurity">Salvando...</template>
                                <template v-else>Salvar Alterações</template>
                            </sw-button>
                        </div>
                    </form>
                </template>

                <!-- Tab: Notificações -->
                <template #notifications>
                    <form @submit.prevent="handleSaveNotifications" class="profileUser_form">
                        <div class="modal-section">
                            <h4>Preferências de Notificação</h4>

                            <div class="row">
                                <div class="grid_col_12">
                                    <div class="profileUser_notificationGroup">
                                        <h5>Notificações por E-mail</h5>
                                        <div class="profileUser_notificationItem">
                                            <sw-checkbox
                                                v-model="notificationData.email.documentExpiry"
                                                label="Documentos vencendo"
                                                icon="bell"
                                            />
                                        </div>
                                        <div class="profileUser_notificationItem">
                                            <sw-checkbox
                                                v-model="notificationData.email.newUploads"
                                                label="Novos uploads recebidos"
                                                icon="upload"
                                            />
                                        </div>
                                        <div class="profileUser_notificationItem">
                                            <sw-checkbox
                                                v-model="notificationData.email.weeklyReport"
                                                label="Relatório semanal"
                                                icon="calendar"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="profileUser_actions">
                            <sw-button
                                name="Salvar Preferências"
                                color="blue"
                                :disabled="savingNotifications"
                            >
                                <template v-if="savingNotifications">Salvando...</template>
                                <template v-else>Salvar Preferências</template>
                            </sw-button>
                        </div>
                    </form>
                </template>
            </TabsComponent>
        </Box>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';
    import { notify } from 'sysweb-vue-components/src/global';

    import Box from '@Component/boxs/BoxComponent.vue';
    import TabsComponent from '@Component/tabs/TabsComponent.vue';
    import PlanResources from '@Component/PlanResources.vue';
    import AlertMessage from '@Component/alerts/AlertMessage.vue';

    import {
        BOX_VARIANTS,
        PLAN_TYPES,
        ALERT_VARIANTS
    } from '@/constants';

    import { mockPlans } from '@/data/mockData';

    // Estados dos formulários
    const savingGeneral = ref(false);
    const savingUrl = ref(false);
    const savingSecurity = ref(false);
    const savingNotifications = ref(false);

    // Dados dos formulários
    const generalData = ref({
        firstName: 'João',
        lastName: 'Silva',
        email: 'joao.silva@email.com',
        phone: '(11) 99999-9999',
        cpfCnpj: '123.456.789-00',
        company: 'TechCorp Solutions'
    });

    const urlSettings = ref({
        defaultSlug: 'techcorp'
    });

    const securityData = ref({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorEnabled: false
    });

    const notificationData = ref({
        email: {
            documentExpiry: true,
            newUploads: true,
            weeklyReport: false
        }
    });

    // Plano atual e uso
    const currentPlan = computed(() => {
        return mockPlans.find(plan => plan.type === PLAN_TYPES.PROFESSIONAL) || mockPlans[0];
    });

    const currentUsage = ref({
        storage: { used: 15.2, total: 100 },
        emails: { used: 850, total: 1000 },
        alerts: { used: 12, total: 50 },
        sharingPages: { used: 8, total: 25 },
        uploadPages: { used: 3, total: 10 }
    });

    // Configuração dos recursos para o componente PlanResources
    const planResourcesConfig = computed(() => ({
        storage: currentUsage.value.storage.total,
        sharingPages: currentUsage.value.sharingPages.total,
        uploadPages: currentUsage.value.uploadPages.total,
        emailsSent: currentUsage.value.emails.total,
        alerts: currentUsage.value.alerts.total
    }));

    // Configuração das tabs
    const tabsConfig = computed(() => [
        {
            id: 'general',
            label: 'Geral',
            icon: 'user'
        },
        {
            id: 'url-settings',
            label: 'URLs',
            icon: 'link'
        },
        {
            id: 'plan',
            label: 'Plano',
            icon: 'briefcase'
        },
        {
            id: 'security',
            label: 'Segurança',
            icon: 'shield'
        },
        {
            id: 'notifications',
            label: 'Notificações',
            icon: 'notification'
        }
    ]);

    // Máscara dinâmica para CPF/CNPJ
    const cpfCnpjMask = computed(() => {
        const cleanValue = generalData.value.cpfCnpj.replace(/\D/g, '');
        if (cleanValue.length <= 11) {
            return '000.000.000-00'; // CPF
        } else {
            return '00.000.000/0000-00'; // CNPJ
        }
    });

    // Validações
    const cpfCnpjValidation = computed(() => {
        const value = generalData.value.cpfCnpj.replace(/\D/g, '');
        const isValidLength = value.length === 11 || value.length === 14;

        if (value.length === 0) {
            return { isValid: false, error: { message: '' } };
        }

        if (!isValidLength) {
            return {
                isValid: false,
                error: { message: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos' }
            };
        }

        // Validação básica de CPF
        if (value.length === 11) {
            // Verifica se todos os dígitos são iguais
            if (/^(\d)\1{10}$/.test(value)) {
                return {
                    isValid: false,
                    error: { message: 'CPF inválido' }
                };
            }
        }

        return { isValid: true, error: { message: '' } };
    });

    const firstNameValidation = computed(() => ({
        isValid: generalData.value.firstName.trim().length >= 2,
        error: {
            message: generalData.value.firstName.trim().length > 0 && generalData.value.firstName.trim().length < 2
                ? 'Nome deve ter pelo menos 2 caracteres'
                : ''
        }
    }));

    const lastNameValidation = computed(() => ({
        isValid: generalData.value.lastName.trim().length >= 2,
        error: {
            message: generalData.value.lastName.trim().length > 0 && generalData.value.lastName.trim().length < 2
                ? 'Sobrenome deve ter pelo menos 2 caracteres'
                : ''
        }
    }));

    const emailValidation = computed(() => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return {
            isValid: emailRegex.test(generalData.value.email),
            error: {
                message: generalData.value.email.length > 0 && !emailRegex.test(generalData.value.email)
                    ? 'E-mail inválido'
                    : ''
            }
        };
    });

    const slugValidation = computed(() => {
        const slugRegex = /^[a-z][a-z0-9-]*[a-z0-9]$/;
        const slug = urlSettings.value.defaultSlug;
        const isValidLength = slug.length >= 3 && slug.length <= 30;
        const isValidFormat = slugRegex.test(slug);

        return {
            isValid: isValidLength && isValidFormat,
            error: {
                message: slug.length > 0 && (!isValidLength || !isValidFormat)
                    ? 'URL deve seguir as regras especificadas'
                    : ''
            }
        };
    });

    const newPasswordValidation = computed(() => ({
        isValid: securityData.value.newPassword.length >= 8,
        error: {
            message: securityData.value.newPassword.length > 0 && securityData.value.newPassword.length < 8
                ? 'Senha deve ter pelo menos 8 caracteres'
                : ''
        }
    }));

    const confirmPasswordValidation = computed(() => ({
        isValid: securityData.value.confirmPassword === securityData.value.newPassword,
        error: {
            message: securityData.value.confirmPassword.length > 0 && securityData.value.confirmPassword !== securityData.value.newPassword
                ? 'Senhas não conferem'
                : ''
        }
    }));

    // Validações dos formulários
    const isGeneralFormValid = computed(() => {
        return firstNameValidation.value.isValid &&
               lastNameValidation.value.isValid &&
               emailValidation.value.isValid &&
               cpfCnpjValidation.value.isValid;
    });

    const isUrlFormValid = computed(() => {
        return slugValidation.value.isValid;
    });

    const isSecurityFormValid = computed(() => {
        if (!securityData.value.currentPassword || !securityData.value.newPassword) return false;
        return newPasswordValidation.value.isValid && confirmPasswordValidation.value.isValid;
    });


    // Methods
    const formatPrice = (price) => {
        return price.toFixed(2).replace('.', ',');
    };

    const handleTabChange = (tabId) => {
        console.log('Tab alterada para:', tabId);
    };

    const handleSaveGeneral = async () => {
        if (!isGeneralFormValid.value) return;

        try {
            savingGeneral.value = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            notify('Informações pessoais atualizadas com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao salvar informações:', error);
            notify('Erro ao atualizar informações. Tente novamente.', 'error');
        } finally {
            savingGeneral.value = false;
        }
    };

    const handleSaveUrlSettings = async () => {
        if (!isUrlFormValid.value) return;

        try {
            savingUrl.value = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            notify('Configuração de URL atualizada com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao salvar configuração de URL:', error);
            notify('Erro ao atualizar configuração. Tente novamente.', 'error');
        } finally {
            savingUrl.value = false;
        }
    };

    const handleSaveSecurity = async () => {
        if (!isSecurityFormValid.value) return;

        try {
            savingSecurity.value = true;
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Limpar campos de senha
            securityData.value.currentPassword = '';
            securityData.value.newPassword = '';
            securityData.value.confirmPassword = '';

            notify('Senha alterada com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao alterar senha:', error);
            notify('Erro ao alterar senha. Tente novamente.', 'error');
        } finally {
            savingSecurity.value = false;
        }
    };

    const handleSaveNotifications = async () => {
        try {
            savingNotifications.value = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            notify('Preferências de notificação atualizadas!', 'success');
        } catch (error) {
            console.error('Erro ao salvar notificações:', error);
            notify('Erro ao atualizar preferências. Tente novamente.', 'error');
        } finally {
            savingNotifications.value = false;
        }
    };

    const changePlan = () => {
        notify('Redirecionando para alteração de plano...', 'info');
        // TODO: Implementar navegação para página de planos
    };


    const handleResourcesUpdate = (newResources) => {
        console.log('Recursos atualizados:', newResources);
        // Aqui você pode implementar a lógica para atualizar os recursos
        // Por exemplo, salvar no backend ou atualizar o estado local
    };

    const handleExtraCostUpdate = (extraCost) => {
        console.log('Custo extra atualizado:', extraCost);
        // Aqui você pode implementar a lógica para lidar com o custo extra
        // Por exemplo, exibir notificações ou atualizar interfaces relacionadas
    };

    onMounted(() => {
        console.log('ProfileUser montado');
    });
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .profileUser {
        display: flex;
        flex-direction: column;
        gap: 1.25em;
        padding: 2em;

        @include screen_medias(s, m) {
            padding: 1em;
            gap: 1em;
        }
    }

    .profileUser_form {
        display: flex;
        flex-direction: column;
        gap: 2em;
    }

    .profileUser_actions {
        display: flex;
        justify-content: flex-start;
        gap: 1em;
        padding-top: 1.5em;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);
    }

    .profileUser_urlPreview {
        margin-top: 1em;
        padding: 1em;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
        border: 1px solid rgba(59, 130, 246, 0.1);
        border-radius: 0.5em;
        font-size: 0.875em;
    }

    .profileUser_urlExample {
        margin-top: 0.5em;
        font-family: monospace;
        padding: 0.5em;
        background: rgba(59, 130, 246, 0.08);
        border-radius: 0.25em;
    }

    .profileUser_urlBase {
        color: var(--textSecondary, #64748b);
    }

    .profileUser_urlSlug {
        color: var(--colorBlue, #3b82f6);
        font-weight: 600;
    }

    .profileUser_urlSuffix {
        color: var(--textSecondary, #64748b);
    }

    .profileUser_urlAlert {
        margin: 24px 0;
    }

    .profileUser_urlRules {
        display: flex;
        align-items: flex-start;
        gap: 1em;
        padding: 1.25em;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
        border: 1px solid rgba(59, 130, 246, 0.1);
        border-radius: 0.75em;
        color: var(--textSecondary, #64748b);

        .sw-icon {
            color: var(--colorBlue, #3b82f6);
            font-size: 1.25em;
            flex-shrink: 0;
            margin-top: 0.125em;
        }

        p {
            margin: 0 0 0.75em 0;
            font-size: 0.875em;
            line-height: 1.6;

            strong {
                color: var(--textPrimary, #1f2937);
            }
        }

        ul {
            margin: 0;
            padding-left: 1.25em;
            font-size: 0.875em;
            line-height: 1.6;

            li {
                margin-bottom: 0.375em;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
    }

    .profileUser_currentPlan {
        padding: 1.5em;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
        border: 1px solid rgba(59, 130, 246, 0.1);
        border-radius: 0.875em;
        margin-bottom: 2em;
    }

    .profileUser_planInfo {
        display: flex;
        align-items: center;
        gap: 1.5em;
    }

    .profileUser_planIcon {
        width: 4em;
        height: 4em;
        border-radius: 1em;
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5em;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .profileUser_planDetails {
        flex: 1;

        h5 {
            margin: 0 0 0.5em 0;
            font-size: 1.25em;
            font-weight: 700;
            color: var(--textPrimary, #1f2937);
        }

        p {
            margin: 0 0 1em 0;
            font-size: 0.875em;
            color: var(--textSecondary, #64748b);
            line-height: 1.4;
        }
    }

    .profileUser_planPrice {
        display: flex;
        align-items: baseline;
        gap: 0.25em;
    }

    .profileUser_price {
        font-size: 1.5em;
        font-weight: 700;
        color: var(--colorBlue, #3b82f6);
    }

    .profileUser_period {
        font-size: 0.875em;
        color: var(--textSecondary, #64748b);
    }

    .profileUser_planActions {
        flex-shrink: 0;
    }

    .profileUser_usageStat {
        display: flex;
        align-items: flex-start;
        gap: 1em;
        padding: 1.25em;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.6));
        border: 1px solid rgba(226, 232, 240, 0.8);
        border-radius: 0.875em;
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
    }

    .profileUser_statIcon {
        width: 2.5em;
        height: 2.5em;
        border-radius: 0.5em;
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1em;
        flex-shrink: 0;
    }

    .profileUser_statContent {
        flex: 1;
        min-width: 0;
    }

    .profileUser_statNumber {
        display: block;
        font-size: 1.125em;
        font-weight: 700;
        color: var(--textPrimary, #1f2937);
        margin-bottom: 0.25em;
    }

    .profileUser_statLabel {
        display: block;
        font-size: 0.75em;
        color: var(--textSecondary, #64748b);
        margin-bottom: 0.75em;
        line-height: 1.3;
    }

    .profileUser_progressBar {
        width: 100%;
        height: 0.375em;
        background: rgba(226, 232, 240, 0.6);
        border-radius: 0.1875em;
        overflow: hidden;
    }

    .profileUser_progress {
        height: 100%;
        background: linear-gradient(90deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        border-radius: 0.1875em;
        transition: width 0.3s ease;
    }


    .profileUser_notificationGroup {
        h5 {
            margin: 0 0 1em 0;
            font-size: 1em;
            font-weight: 600;
            color: var(--textPrimary, #1f2937);
            padding-bottom: 0.5em;
            border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
        }
    }

    .profileUser_notificationItem {
        margin-bottom: 1.5em;
    }


    @include screen_medias(s, m) {
        .profileUser_planInfo {
            flex-direction: column;
            align-items: flex-start;
            text-align: center;
            gap: 1em;
        }

        .profileUser_planActions {
            width: 100%;
        }

        .profileUser_actions {
            flex-direction: column;
        }

        .profileUser_statContent {
            text-align: center;
        }
    }
</style>